import React, { useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import { Spinner } from '@fluentui/react-components';


import { MetricPicker } from './components/index';
import { NoData } from './components/no-data';
import { useStyles } from './styles/performance-card';
import { withHighchartsChart, withFluentChart } from './components/with-chart';
import { IPerformanceCardProps } from './types/types';


const ChartView = React.memo(({
  classes, hasError, isLoading,
  chartProps, Chart, i18n, shellContentElement,
}: {
  classes: any,
  hasError: boolean,
  isLoading: boolean,
  chartProps: any,
  i18n: any,
  Chart: any,
  shellContentElement: Element,
}) => {
  const ref = useRef<any>(null);
  const reflow = useCallback(_.debounce(() => {
    setTimeout(() => {
      if (ref.current) {
        ref.current.chart.reflow();
      }
    }, 0);
  }, 10), []);
  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(reflow);
    resizeObserver.observe(shellContentElement);

    return () => resizeObserver.disconnect();
  }, [reflow, shellContentElement]);


  if (hasError) {
    return <NoData i18n={i18n} showErrorText />;
  }
  if (isLoading) {
    return (
      <Spinner
        className={classNames('ba-performance-card-fluent-v2__spinner', classes.spinner)}
        size="small"
        labelPosition="below"
        label={i18n.getString(_TL_('Connecting to data'))}
      />
    );
  }
  if (chartProps?.hasChartData) {
    return <Chart ref={ref} {...chartProps} />;
  }

  return <NoData i18n={i18n} />;
});

export function PerformanceCardInternal(props: IPerformanceCardProps) {
  const classes = useStyles();
  const {
    selectedMetricIndexes,
    visibleMetrics,
    aggregatedMetricPickerData,
    granularity,
    hideCurrency,
    TimeZone,
    performanceTimeSeries,
    isComparisonEnabled,
    onMetricUpdate,
    isLoading,
    hasError,
    i18n,
    activity,
    responsiveSize,
    Chart,
    useChartProps,
    shellContentElement,
  } = props;

  const visibleMetricsDisplayNames = useMemo(() => {
    if (_.isEmpty(aggregatedMetricPickerData)) {
      return visibleMetrics;
    }
    return visibleMetrics.map((metric) => {
      const displayName = aggregatedMetricPickerData.find(item => item.key === metric)?.displayName;

      if (!displayName) {
        activity.error(`Missing aggregatedMetricPickerData for ${metric} in fluent perf chart. This may be bug or polluted preference?`);
      }

      return displayName || metric;
    });
  }, [activity, aggregatedMetricPickerData, visibleMetrics]);

  const chartProps = useChartProps({
    i18n,
    selectedMetricIndexes,
    visibleMetrics,
    visibleMetricsDisplayNames,
    performanceTimeSeries,
    granularity,
    hideCurrency,
    TimeZone,
  });

  // TODO: old chart creates new instance every time when redraw, so it has different animation. Do we need to follow this practice in React?
  return (
    <div className={classNames('ba-performance-card-fluent-v2', classes.cardContent)}>
      <MetricPicker
        selectedMetricIndexes={selectedMetricIndexes}
        visibleMetrics={visibleMetrics}
        allMetrics={aggregatedMetricPickerData}
        isComparisonEnabled={isComparisonEnabled}
        onMetricUpdate={onMetricUpdate}
        responsiveSize={responsiveSize}
        i18n={i18n}
        hideCurrency={hideCurrency}
      />
      <div className={classNames('ba-performance-card-fluent-v2__chart', classes.chartContainer)}>
        <ChartView
          classes={classes}
          hasError={hasError}
          isLoading={isLoading}
          chartProps={chartProps}
          Chart={Chart}
          i18n={i18n}
          shellContentElement={shellContentElement}
        />
      </div>
    </div>
  );
}

PerformanceCardInternal.defaultProps = {
  isLoading: false,
  hasError: false,
  hideCurrency: false,
};

export const PerformanceCard = withHighchartsChart(PerformanceCardInternal);
export const PerformanceCardFluent = withFluentChart(PerformanceCardInternal);
